import { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import { functions } from '../firebase/config';
import Layout from '../components/layout/Layout';

const CheckIcon = () => (
  <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const StarIcon = ({ className = 'w-8 h-8 text-amber-500 dark:text-amber-400' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ZapIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const PREMIUM_PRICES = { monthly: 4999, yearly: 49999 };

const PremiumPage = () => {
  const { userData, currentUser } = useAuth();
  const isPremium = userData?.isPremium === true;
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);

  const handleSubscribe = async (planId) => {
    if (!currentUser?.uid) {
      setError('Iniciá sesión para suscribirte');
      return;
    }
    setError(null);
    setLoadingPlan(planId);
    try {
      const isLocalhost = window.location.hostname === 'localhost';
      let data;

      if (isLocalhost) {
        // Desde localhost la callable puede dar CORS; usamos el endpoint HTTP con CORS
        const token = await currentUser.getIdToken();
        const url = 'https://us-central1-illustra-6ca8a.cloudfunctions.net/createPremiumSubscriptionHTTP';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ userId: currentUser.uid, planId })
        });
        data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || data?.error || 'Error al crear suscripción');
        }
      } else {
        const createPremiumSubscription = httpsCallable(functions, 'createPremiumSubscription');
        const result = await createPremiumSubscription({ userId: currentUser.uid, planId });
        data = result.data;
      }

      if (data?.success && data?.paymentUrl) {
        window.location.href = data.paymentUrl;
        return;
      }
      setError(data?.message || 'No se pudo crear el enlace de pago');
    } catch (err) {
      const msg = err?.details || err?.message || err?.code || 'Error al crear suscripción';
      setError(typeof msg === 'string' ? msg : 'Error al crear suscripción');
    } finally {
      setLoadingPlan(null);
    }
  };

  const benefits = [
    { 
      icon: <StarIcon />,
      title: 'Badge Premium Exclusivo', 
      description: 'Un distintivo dorado que resalta tu perfil y genera confianza en potenciales clientes.'
    },
    { 
      icon: <TrendingUpIcon />,
      title: 'Visibilidad Prioritaria', 
      description: 'Aparece primero en búsquedas y en la sección "Artistas Destacados" de la home.'
    },
    { 
      icon: <ShieldIcon />,
      title: 'Personalización completa de perfil', 
      description: 'Personaliza tu perfil con más opciones visuales (colores, fondo y elementos destacados) para que refleje tu estilo.'
    },
    { 
      icon: <ZapIcon />,
      title: 'Más Servicios Activos', 
      description: 'Publica servicios sin límite (vs. 5 en cuenta gratuita).'
    },
    { 
      icon: <ZapIcon />,
      title: 'Series Ampliadas', 
      description: 'Crea series ilimitadas con episodios ilimitados para mostrar tu trabajo.'
    },
    { 
      icon: <ShieldIcon />,
      title: 'Soporte Prioritario', 
      description: 'Respuesta en menos de 24hs para cualquier consulta o problema técnico.'
    }
  ];

  const comparison = [
    { feature: 'Servicios activos', free: '5', premium: 'Ilimitado' },
    { feature: 'Series permitidas', free: '2', premium: 'Ilimitado' },
    { feature: 'Episodios por serie', free: '10', premium: 'Ilimitado' },
    { feature: 'Badge distintivo', free: '—', premium: '✓' },
    { feature: 'Personalización de perfil', free: '—', premium: '✓' },
    { feature: 'Prioridad en búsquedas', free: '—', premium: '✓' },
    { feature: 'Sección destacados', free: '—', premium: '✓' },
    { feature: 'Soporte prioritario', free: '—', premium: '✓' }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="w-full bg-gradient-to-b from-indigo-500/10 via-purple-500/10 to-transparent dark:from-indigo-900/20 dark:via-purple-900/20 py-8 sm:py-12">
        <div className="container-custom max-w-4xl text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl shadow-lg">
              <StarIcon className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-2">
            Illustra Premium
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Lleva tu perfil al siguiente nivel. Más visibilidad, más oportunidades, más clientes.
          </p>
          
          {isPremium && (
            <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 inline-block">
              <div className="flex items-center gap-2 mb-1">
                <CheckIcon />
                <p className="text-green-800 dark:text-green-200 font-semibold text-base">
                  ¡Ya sos miembro Premium!
                </p>
              </div>
              <p className="text-green-700 dark:text-green-300 text-sm mb-3">
                Estás disfrutando de todos los beneficios exclusivos.
              </p>
              <Link 
                to="/profile" 
                className="inline-flex items-center px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors"
              >
                Ver mi perfil
              </Link>
            </div>
          )}
        </div>
      </section>

      {!isPremium && (
        <>
          {/* Planes — primero para captar la acción */}
          <section className="container-custom py-8 sm:py-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-1">
              Elegí tu plan
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-400 text-sm mb-6">
              Precios accesibles para potenciar tu carrera como creador
            </p>
            
            {error && (
              <div className="max-w-4xl mx-auto mb-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm text-center">
                {error}
              </div>
            )}
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-4 md:gap-6">
              {/* Plan Mensual */}
              <div className="p-5 sm:p-6 rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                <div className="mb-4">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1">
                    Mensual
                  </h3>
                  <p className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                    ${PREMIUM_PRICES.monthly.toLocaleString('es-AR')}/mes
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Facturación mensual • Cancela cuando quieras
                  </p>
                </div>
                
                <ul className="space-y-2 mb-5 flex-grow">
                  <li className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Todos los beneficios Premium
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Sin permanencia mínima
                    </span>
                  </li>
                </ul>
                
                <button
                  type="button"
                  onClick={() => handleSubscribe('monthly')}
                  disabled={loadingPlan != null}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-60"
                >
                  {loadingPlan === 'monthly' ? 'Creando enlace…' : currentUser ? 'Suscribirme con Mercado Pago' : 'Iniciar sesión para suscribirte'}
                </button>
              </div>

              {/* Plan Anual */}
              <div className="p-5 sm:p-6 rounded-2xl border-2 border-indigo-500 dark:border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 relative flex flex-col shadow-lg hover:shadow-xl transition-shadow">
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-semibold shadow-md">
                  ⚡ Mejor valor
                </div>
                
                <div className="mb-4">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1">
                    Anual
                  </h3>
                  <p className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                    ${PREMIUM_PRICES.yearly.toLocaleString('es-AR')}/año
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Ahorra 20% • Facturación anual
                  </p>
                </div>
                
                <ul className="space-y-2 mb-5 flex-grow">
                  <li className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Todos los beneficios Premium
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>2 meses gratis</strong> vs. plan mensual
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckIcon />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Prioridad máxima en soporte
                    </span>
                  </li>
                </ul>
                
                <button
                  type="button"
                  onClick={() => handleSubscribe('yearly')}
                  disabled={loadingPlan != null}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg disabled:opacity-60"
                >
                  {loadingPlan === 'yearly' ? 'Creando enlace…' : currentUser ? 'Suscribirme con Mercado Pago' : 'Iniciar sesión para suscribirte'}
                </button>
              </div>
            </div>
          </section>

          {/* Beneficios destacados */}
          <section className="container-custom max-w-5xl py-8 sm:py-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-6">
              ¿Qué incluye Premium?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((item, i) => (
                <div 
                  key={i} 
                  className="p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-md transition-shadow"
                >
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-2">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Comparación */}
          <section className="container-custom max-w-4xl py-8 sm:py-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-1">
              Gratuito vs Premium
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-400 text-sm mb-6">
              Descubre todo lo que podés lograr con Premium
            </p>
            
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[280px]">
                  <thead className="bg-gray-50 dark:bg-slate-900/50">
                    <tr>
                      <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                        Característica
                      </th>
                      <th className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400">
                        Gratuito
                      </th>
                      <th className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                        Premium
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                    {comparison.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-900/30">
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 dark:text-white">
                          {row.feature}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-center text-gray-600 dark:text-gray-400">
                          {row.free}
                        </td>
                        <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-center font-medium text-indigo-600 dark:text-indigo-400">
                          {row.premium}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="container-custom max-w-3xl py-12 sm:py-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-10">
              Preguntas frecuentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: '¿Cómo me suscribo?',
                  a: 'Elegí el plan mensual o anual y hacé clic en "Suscribirme con Mercado Pago". Serás redirigido a Mercado Pago para completar el pago de forma segura. La suscripción se activa al confirmar el pago.'
                },
                {
                  q: '¿Puedo cancelar cuando quiera?',
                  a: 'Sí, podés cancelar tu suscripción en cualquier momento. Seguirás disfrutando de Premium hasta el final del período pagado.'
                },
                {
                  q: '¿La plataforma cobra comisión por mis servicios?',
                  a: 'No. Los pagos entre usuarios son directos. Premium solo mejora tu visibilidad y capacidades en la plataforma.'
                },
                {
                  q: '¿Qué pasa si cambio de plan?',
                  a: 'Podés cambiar entre mensual y anual cuando quieras. Se ajustará el precio prorrateado en tu próxima factura.'
                }
              ].map((item, i) => (
                <details 
                  key={i}
                  className="group p-6 rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-md transition-shadow"
                >
                  <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer list-none flex items-center justify-between">
                    {item.q}
                    <svg 
                      className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Footer disclaimer */}
      <footer className="container-custom py-5 mt-4 border-t border-gray-200 dark:border-slate-800">
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center max-w-2xl mx-auto leading-snug">
          Suscripción Premium mediante Mercado Pago (cobro recurrente). Los pagos por servicios entre usuarios siguen siendo directos, sin comisión de la plataforma.
        </p>
      </footer>
    </Layout>
  );
};

export default PremiumPage;